import { PackageType } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

interface PackageItem {
  packageType?: string;
}

/**
 * Determines whether the API Reference dependency chain warning should display.
 * Shows when one or more of the 4 API Reference categories are present but not all 4.
 * Hides when at least one from each category exists (full chain complete).
 *
 * Categories:
 * 1. Service Model
 * 2. Doc Model
 * 3. C2J or External Model
 * 4. ApiDoc (API_REFERENCE_SMITHY or API_REFERENCE_REEF)
 *
 * API_REFERENCE_STRUT is intentionally excluded: Strut is for API Gateway services
 * that have no Coral/Smithy model. It pulls the Swagger model directly from API
 * Gateway and is self-contained (no Service Model / Doc Model / External Model / C2J
 * packages). So a Strut ApiDoc has no dependency chain and must not trigger this warning.
 */
export const shouldShowApiRefDependencyWarning = (
  items: PackageItem[]
): boolean => {
  const hasService = items.some(
    (p) => p.packageType === PackageType.SERVICE_MODEL
  );
  const hasDoc = items.some((p) => p.packageType === PackageType.DOC_MODEL);
  const hasC2J = items.some((p) => p.packageType === PackageType.C2J);
  const hasExternal = items.some(
    (p) => p.packageType === PackageType.EXTERNAL_MODEL
  );
  const hasApiDoc = items.some(
    (p) =>
      p.packageType === PackageType.API_REFERENCE_SMITHY ||
      p.packageType === PackageType.API_REFERENCE_REEF
  );

  const apiRefTypes = [hasService, hasDoc, hasC2J || hasExternal, hasApiDoc];
  const hasAnyApiRefType = apiRefTypes.some(Boolean);
  const hasAllApiRefTypes = apiRefTypes.every(Boolean);

  return hasAnyApiRefType && !hasAllApiRefTypes;
};
